import React, { useState, useEffect, useContext } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Box,
  CircularProgress,
  TextField,
  Autocomplete,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  getAirports,
  addTransportationRequisition,
  addGuideTransportationRequisition,
} from '../../api';
import { AuthContext } from '../../context/AuthContext';
import { useAlertSnackbar } from '../../hooks/useAlertSnackbar';

interface Airport {
  id: number;
  name: string;
  code: string;
}

interface AddEditGuidanceTourHealthDisabilityDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (isSuccess: boolean) => void;
  salesIndividualId: number;
  direction: string;
  guideSalesIndividualId?: string;
}

export function AddTransportationRequisitionDialog({
  open,
  onClose,
  onSave,
  salesIndividualId,
  direction,
  guideSalesIndividualId,
}: AddEditGuidanceTourHealthDisabilityDialogProps) {
  const { t } = useTranslation();
  const { token } = useContext(AuthContext);
  const [loading, setLoading] = useState<boolean>(false);
  const [airports, setAirports] = useState<Airport[]>([]);
  const [selectedDepartureAirport, setSelectedDepartureAirport] =
    useState<Airport | null>(null);
  const [selectedArrivalAirport, setSelectedArrivalAirport] =
    useState<Airport | null>(null);

  const [plnCode] = useState<number>(2); // PLN code

  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');

  const showSnackbar = useAlertSnackbar();

  const fetchAirport = async () => {
    setLoading(true);
    try {
      const res = await getAirports(token);
      const data = Array.isArray(res) ? res : await res.json();

      if (Array.isArray(data)) {
        setAirports(data);
      } else {
        throw new Error('Invalid API response');
      }
    } catch (e: any) {
      showSnackbar(e.detail || t('loginPage.registerErrorMessage'), 'error');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (open) {
      fetchAirport();
    }
  }, [open, salesIndividualId]);

  const handleSave = async () => {
    if (!selectedDepartureAirport || !selectedArrivalAirport) {
      showSnackbar(
        'Please select both departure and arrival airports.',
        'error',
      );
      return;
    }
    setLoading(true);

    try {
      let response;

      if (!guideSalesIndividualId) {
        response = await addTransportationRequisition(
          token, // authToken
          selectedArrivalAirport.id,
          selectedDepartureAirport.id,
          direction,
          endDate,
          startDate,
          plnCode, // transportationTypeId
          salesIndividualId, // salesIndividualId
        );
      } else {
        response = await addGuideTransportationRequisition(
          token, // authToken
          selectedArrivalAirport.id,
          selectedDepartureAirport.id,
          direction,
          endDate,
          startDate,
          plnCode, // transportationTypeId
          salesIndividualId, // salesIndividualId
          guideSalesIndividualId,
        );
      }

      // Now check 'response' in a single place
      if (!response.ok) {
        const errorData = await response.json();
        showSnackbar(
          errorData?.detail || 'Error saving transportation requisition',
          'error',
        );
      } else {
        onClose();
        onSave(true);
      }
    } catch (error: any) {
      console.error('API error:', error);
      showSnackbar(
        error?.message || 'Error saving transportation requisition',
        'error',
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('transportInfoDetails.title')}</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" p={2}>
            <CircularProgress />
          </Box>
        ) : (
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <Autocomplete
              options={airports}
              getOptionLabel={(option) => option.name}
              value={selectedDepartureAirport}
              onChange={(_, newValue: Airport | null) => {
                setSelectedDepartureAirport(newValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('transportInfoDetails.departureAirport')}
                  placeholder={t('transportInfoDetails.searchAirport')}
                />
              )}
            />

            <Autocomplete
              options={airports}
              getOptionLabel={(option) => option.name}
              value={selectedArrivalAirport}
              onChange={(_, newValue: Airport | null) => {
                setSelectedArrivalAirport(newValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('transportInfoDetails.arrivalAirport')}
                  placeholder={t('transportInfoDetails.searchAirport')}
                />
              )}
            />

            <TextField
              type="date"
              label={t('transportInfoDetails.startDate')}
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              type="date"
              label={t('transportInfoDetails.endDate')}
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        )}
        <Typography>{t('transportInfoDetails.infoMessage')}</Typography>
      </DialogContent>
      <DialogActions sx={{ padding: '16px' }}>
        <Button onClick={onClose} color="error">
          {t('accountInfosPage.cancel')}
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {t('accountInfosPage.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
